import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import CommonButton from 'app/components/ReviewButton';
import { TRANSLATION_KEYS } from 'app/constants/transalations';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './DataCompositionModal.module.scss';

type DataCompositionTableProps = {
  defectName: string;
  total: number;
  train: number;
  test: number;
};

const schema: {
  id: string;
  label: string;
  width: string;
  selector: (r: DataCompositionTableProps) => string | number;
}[] = [
  {
    id: 'defectName',
    label: 'Defect name',
    width: '40%',
    selector: r => r.defectName,
  },
  { id: 'total', label: 'Total', width: '20%', selector: r => r.total },
  { id: 'train', label: 'Train', width: '20%', selector: r => r.train },
  { id: 'test', label: 'Test', width: '20%', selector: r => r.test },
];

interface DataCompositionModalProps {
  isDataCompositionModalOpen: boolean;
  onClose: () => void;
  train_counts: Record<string, number>;
  test_counts: Record<string, number>;
}

export const DataCompositionModal = ({
  isDataCompositionModalOpen,
  onClose,
  train_counts,
  test_counts,
}: DataCompositionModalProps) => {
  const { t } = useTranslation([
    TRANSLATION_KEYS.REVIEW,
    TRANSLATION_KEYS.COMMON,
    TRANSLATION_KEYS.MODEL_LIBRARY,
  ]);

  const rows = useMemo(() => {
    const allDefects = new Set([
      ...Object.keys(train_counts),
      ...Object.keys(test_counts),
    ]);
    return Array.from(allDefects).map(defectName => {
      const train = train_counts[defectName] ?? 0;
      const test = test_counts[defectName] ?? 0;
      return { defectName, train, test, total: train + test };
    });
  }, [train_counts, test_counts]);

  return (
    <Dialog
      open={isDataCompositionModalOpen}
      onClose={onClose}
      classes={{ paper: classes.paper }}
      BackdropProps={{ className: classes.backdrop }}
    >
      <DialogTitle disableTypography className={classes.headerContainer}>
        <span className={classes.header}>{t('Data Composition')}</span>
        <button className={classes.closeIcon} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </DialogTitle>
      <DialogContent className={classes.modalBody}>
        <table className={classes.table}>
          <thead>
            <tr>
              {schema.map(col => (
                <th
                  key={col.id}
                  className={classes.tableHeader}
                  style={{ width: col.width }}
                >
                  {t(col.label)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={classes.tableBody}>
            {rows.map(row => (
              <tr key={row.defectName}>
                {schema.map(col => (
                  <td
                    key={col.id}
                    className={classes.tableData}
                    style={{ width: col.width }}
                  >
                    {col.selector(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </DialogContent>
      <DialogActions className={classes.modalActionBtns}>
        <CommonButton
          onClick={onClose}
          text={t('Okay')}
          variant='primary'
          size='xs'
          wrapperClass={classes.runButton}
          dataTestId='data-composition--button'
        />
      </DialogActions>
    </Dialog>
  );
};
